import Phaser from 'phaser';
import redBall from '../static/red.png';
import greenBall from '../static/green.png';
import blueBall from '../static/blue.png';
import yellowBall from '../static/yellow.png';
import purpleBall from '../static/purple.png';
import tealBall from '../static/teal.png';
import pinkBall from '../static/pink.png';

export default class GameScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameScene' });
        this.gridSize = 9;
        this.cellSize = 90;

        // Calculate margins and board dimensions
        this.margin = Math.floor(this.cellSize / 3); // Smaller margin
        this.boardOriginX = this.margin;
        this.boardOriginY = this.margin + 60; // Account for top bar

        // Calculate total dimensions
        this.totalWidth = (this.gridSize * this.cellSize) + (this.margin * 2);
        this.totalHeight = (this.gridSize * this.cellSize) + (this.margin * 2) + 60; // Add space for top bar

        this.colors = [
            'red',
            'green',
            'blue',
            'yellow',
            'purple',
            'teal',
            'pink'
        ];
        this.score = 0;
        this.selectedBall = null;
        this.previewBalls = [];
        this.board = [];
    }

    preload() {
        // Load ball images at high quality
        this.load.image('red', redBall);
        this.load.image('green', greenBall);
        this.load.image('blue', blueBall);
        this.load.image('yellow', yellowBall);
        this.load.image('purple', purpleBall);
        this.load.image('teal', tealBall);
        this.load.image('pink', pinkBall);
    }

    create() {
        // Initialize the game board
        this.createBoard();

        // Setup the HTML UI
        this.setupHtmlUI();

        // Place initial 5 balls
        this.placeInitialBalls();
    }

    setupHtmlUI() {
        // Wait a brief moment to ensure DOM is ready
        setTimeout(() => {
            try {
                // Clear any existing preview balls
                const nextBallsContainer = document.getElementById('next-balls');
                if (!nextBallsContainer) return; // Exit if container not found

                nextBallsContainer.innerHTML = '';

                // Create new preview ball elements
                for (let i = 0; i < 3; i++) {
                    const ballPreview = document.createElement('div');
                    // Add a default color class to ensure texture background is used instead of color
                    const defaultColor = this.colors[Math.floor(Math.random() * this.colors.length)];
                    ballPreview.className = 'ball-preview ' + defaultColor;
                    nextBallsContainer.appendChild(ballPreview);
                }

                // Generate initial preview balls
                this.generatePreviewBalls();

                // Reset score display
                const scoreElement = document.getElementById('score');
                if (scoreElement) {
                    scoreElement.textContent = this.score;
                }
            } catch (error) {
                console.error("Error setting up HTML UI:", error);
            }
        }, 100);
    }

    createBoard() {
        // Create the board grid
        this.board = Array(this.gridSize).fill().map(() => Array(this.gridSize).fill(null));

        // Draw the grid background
        const graphics = this.add.graphics();
        graphics.lineStyle(2, 0x95a5a6, 0.3); // More subtle grid lines
        graphics.fillStyle(0xecf0f1, 1);

        for (let row = 0; row < this.gridSize; row++) {
            for (let col = 0; col < this.gridSize; col++) {
                const x = this.boardOriginX + col * this.cellSize;
                const y = this.boardOriginY + row * this.cellSize;

                graphics.fillRect(x, y, this.cellSize, this.cellSize);
                graphics.strokeRect(x, y, this.cellSize, this.cellSize);

                // Make cells interactive
                const cell = this.add.rectangle(
                    x + this.cellSize / 2,
                    y + this.cellSize / 2,
                    this.cellSize - 2,
                    this.cellSize - 2,
                    0xecf0f1,
                    0
                );
                cell.setInteractive();
                cell.on('pointerdown', () => this.handleCellClick(row, col));
            }
        }

        // Add a game over text (hidden initially)
        this.gameOverText = this.add.text(this.totalWidth / 2, this.totalHeight / 2, 'GAME OVER', {
            fontSize: '64px',
            fill: '#e74c3c',
            fontFamily: 'Arial',
            fontWeight: 'bold',
            stroke: '#2c3e50',
            strokeThickness: 8
        }).setOrigin(0.5, 0.5).setVisible(false);
    }

    handleCellClick(row, col) {
        // If game is over, do nothing
        if (this.isGameOver()) return;

        const cellContent = this.board[row][col];

        // If a ball is already selected
        if (this.selectedBall) {
            const { row: srcRow, col: srcCol } = this.selectedBall;

            // If clicking on the same ball, deselect it
            if (srcRow === row && srcCol === col) {
                this.deselectBall();
                return;
            }

            // If clicking on another ball, select that one instead
            if (cellContent) {
                this.deselectBall();
                this.selectBall(row, col);
                return;
            }

            // If clicking on an empty cell, try to move the ball
            if (!cellContent) {
                this.moveBall(srcRow, srcCol, row, col);
            }
        }
        // If no ball is selected and clicked on a ball, select it
        else if (cellContent) {
            this.selectBall(row, col);
        }
    }

    selectBall(row, col) {
        const cellContent = this.board[row][col];
        if (!cellContent) return;

        this.selectedBall = { row, col, sprite: cellContent.sprite };

        // Create or reuse the selection highlight graphics
        if (!this.selectionHighlight) {
            this.selectionHighlight = this.add.graphics();
        }

        // Clear any previous highlight
        this.selectionHighlight.clear();

        // Draw a more subtle highlight circle
        this.selectionHighlight.lineStyle(2, 0x2c3e50, 0.3);
        const x = cellContent.sprite.x;
        const y = cellContent.sprite.y;
        const radius = (this.cellSize * 0.75) / 2;
        this.selectionHighlight.strokeCircle(x, y, radius);

        this.highlightPossibleMoves(row, col);
    }

    deselectBall() {
        if (this.selectedBall) {
            // Clear the selection highlight
            if (this.selectionHighlight) {
                this.selectionHighlight.clear();
            }
            this.selectedBall = null;
            this.clearHighlights();
        }
    }

    highlightPossibleMoves(startRow, startCol) {
        // Clear any existing highlights
        this.clearHighlights();

        // Find all reachable cells using BFS
        const reachableCells = this.findReachableCells(startRow, startCol);

        // Highlight reachable cells with semi-transparent dark green
        const graphics = this.add.graphics();
        graphics.fillStyle(0x27ae60, 0.3);

        reachableCells.forEach(cell => {
            const { row, col } = cell;
            const x = this.boardOriginX + col * this.cellSize;
            const y = this.boardOriginY + row * this.cellSize;

            graphics.fillRect(x + 5, y + 5, this.cellSize - 10, this.cellSize - 10);
        });

        this.highlightGraphics = graphics;
    }

    clearHighlights() {
        if (this.highlightGraphics) {
            this.highlightGraphics.clear();
            this.highlightGraphics.destroy();
            this.highlightGraphics = null;
        }
    }

    findReachableCells(startRow, startCol) {
        const visited = Array(this.gridSize).fill().map(() => Array(this.gridSize).fill(false));
        const queue = [{ row: startRow, col: startCol, path: [] }];
        const reachable = [];

        visited[startRow][startCol] = true;

        while (queue.length > 0) {
            const { row, col, path } = queue.shift();

            // Define the four possible moves (up, down, left, right)
            const directions = [
                { row: -1, col: 0 }, // Up
                { row: 1, col: 0 },  // Down
                { row: 0, col: -1 }, // Left
                { row: 0, col: 1 }   // Right
            ];

            for (const dir of directions) {
                const newRow = row + dir.row;
                const newCol = col + dir.col;

                // Check if the new position is valid
                if (
                    newRow >= 0 && newRow < this.gridSize &&
                    newCol >= 0 && newCol < this.gridSize &&
                    !visited[newRow][newCol] &&
                    !this.board[newRow][newCol] // Cell must be empty
                ) {
                    visited[newRow][newCol] = true;

                    // Add to reachable cells
                    reachable.push({ row: newRow, col: newCol });

                    // Add to queue for further exploration
                    const newPath = [...path, { row: newRow, col: newCol }];
                    queue.push({ row: newRow, col: newCol, path: newPath });
                }
            }
        }

        return reachable;
    }

    moveBall(fromRow, fromCol, toRow, toCol) {
        // Check if the move is valid
        const reachableCells = this.findReachableCells(fromRow, fromCol);
        const isValidMove = reachableCells.some(cell => cell.row === toRow && cell.col === toCol);

        if (!isValidMove) return;

        // Move the ball
        const ball = this.board[fromRow][fromCol];
        this.board[fromRow][fromCol] = null;
        this.board[toRow][toCol] = ball;

        // Animate the movement
        const x = this.boardOriginX + toCol * this.cellSize + this.cellSize / 2;
        const y = this.boardOriginY + toRow * this.cellSize + this.cellSize / 2;

        this.tweens.add({
            targets: ball.sprite,
            x: x,
            y: y,
            duration: 200,
            ease: 'Power2',
            onComplete: () => {
                // Check for line formations
                const linesFormed = this.checkLines(toRow, toCol);

                if (linesFormed.length > 0) {
                    // Remove balls in lines and update score
                    this.removeLines(linesFormed);

                    // Player gets another turn
                    this.deselectBall();
                } else {
                    // Computer's turn
                    this.deselectBall();
                    this.placePreviewBalls();
                }
            }
        });
    }

    checkLines(row, col) {
        const color = this.board[row][col].color;
        const lines = [];

        // Check horizontal line
        const horizontalLine = this.checkLineInDirection(row, col, 0, 1, color);
        if (horizontalLine.length >= 5) lines.push(horizontalLine);

        // Check vertical line
        const verticalLine = this.checkLineInDirection(row, col, 1, 0, color);
        if (verticalLine.length >= 5) lines.push(verticalLine);

        // Check diagonal (top-left to bottom-right)
        const diag1Line = this.checkLineInDirection(row, col, 1, 1, color);
        if (diag1Line.length >= 5) lines.push(diag1Line);

        // Check diagonal (top-right to bottom-left)
        const diag2Line = this.checkLineInDirection(row, col, 1, -1, color);
        if (diag2Line.length >= 5) lines.push(diag2Line);

        return lines;
    }

    checkLineInDirection(row, col, rowDir, colDir, color) {
        const line = [{ row, col }];

        // Check in positive direction
        let r = row + rowDir;
        let c = col + colDir;

        while (
            r >= 0 && r < this.gridSize &&
            c >= 0 && c < this.gridSize &&
            this.board[r][c] &&
            this.board[r][c].color === color
        ) {
            line.push({ row: r, col: c });
            r += rowDir;
            c += colDir;
        }

        // Check in negative direction
        r = row - rowDir;
        c = col - colDir;

        while (
            r >= 0 && r < this.gridSize &&
            c >= 0 && c < this.gridSize &&
            this.board[r][c] &&
            this.board[r][c].color === color
        ) {
            line.push({ row: r, col: c });
            r -= rowDir;
            c -= colDir;
        }

        return line;
    }

    removeLines(lines) {
        // Create a set of all cells to remove (to avoid duplicates)
        const cellsToRemove = new Set();

        lines.forEach(line => {
            line.forEach(cell => {
                cellsToRemove.add(`${cell.row},${cell.col}`);
            });
        });

        // Calculate score based on number of balls removed
        const pointsPerBall = 10;
        const basePoints = 5 * pointsPerBall; // 5 balls = 50 points
        let totalPoints = 0;

        lines.forEach(line => {
            if (line.length === 5) {
                totalPoints += basePoints;
            } else if (line.length > 5) {
                // Bonus points for lines longer than 5
                totalPoints += basePoints + (line.length - 5) * pointsPerBall * 2;
            }
        });

        // Update score
        this.score += totalPoints;
        this.updateScore();

        // Remove the balls with updated animation colors
        cellsToRemove.forEach(cellStr => {
            const [row, col] = cellStr.split(',').map(Number);

            // Animate ball disappearing with a flash of bright color
            const ball = this.board[row][col];
            if (ball && ball.sprite) {
                // Add a flash effect
                ball.sprite.setTint(0xf1c40f);
                this.tweens.add({
                    targets: ball.sprite,
                    alpha: 0,
                    scale: 1.5,
                    duration: 300,
                    ease: 'Power2',
                    onComplete: () => {
                        ball.sprite.destroy();
                    }
                });
            }

            // Remove from board
            this.board[row][col] = null;
        });
    }

    placeInitialBalls() {
        // Place 5 random balls
        for (let i = 0; i < 5; i++) {
            this.placeRandomBall();
        }
    }

    placeRandomBall() {
        const emptyCells = this.getEmptyCells();
        if (emptyCells.length === 0) return false;

        // Choose a random empty cell
        const index = Math.floor(Math.random() * emptyCells.length);
        const { row, col } = emptyCells[index];

        // Choose a random color
        const color = this.colors[Math.floor(Math.random() * this.colors.length)];

        // Create a ball
        this.createBall(row, col, color);

        return true;
    }

    getEmptyCells() {
        const emptyCells = [];

        for (let row = 0; row < this.gridSize; row++) {
            for (let col = 0; col < this.gridSize; col++) {
                if (!this.board[row][col]) {
                    emptyCells.push({ row, col });
                }
            }
        }

        return emptyCells;
    }

    createBall(row, col, color) {
        // Calculate position based on grid coordinates
        const x = this.boardOriginX + col * this.cellSize + this.cellSize / 2;
        const y = this.boardOriginY + row * this.cellSize + this.cellSize / 2;

        // Create sprite
        const ballSprite = this.add.sprite(x, y, color);
        ballSprite.setOrigin(0.5, 0.5);

        // Calculate optimal size while maintaining aspect ratio
        const targetSize = Math.floor(this.cellSize * 0.85);
        const scale = targetSize / Math.max(ballSprite.width, ballSprite.height);

        // Apply scale
        ballSprite.setScale(scale);

        // Store in board
        this.board[row][col] = { color, sprite: ballSprite };

        // Add appear animation
        ballSprite.setScale(0);
        this.tweens.add({
            targets: ballSprite,
            scaleX: scale,
            scaleY: scale,
            duration: 200,
            ease: 'Back.out'
        });

        return ballSprite;
    }

    generatePreviewBalls() {
        try {
            this.previewBalls = [];
            const previewElements = document.querySelectorAll('.ball-preview');
            if (!previewElements || previewElements.length === 0) return;

            // Generate new preview balls
            for (let i = 0; i < previewElements.length; i++) {
                const colorIndex = Math.floor(Math.random() * this.colors.length);
                const color = this.colors[colorIndex];
                this.previewBalls.push(color);

                // Update preview ball appearance using the corresponding CSS class (which uses background-image)
                const element = previewElements[i];
                element.className = 'ball-preview ' + color;
            }
        } catch (error) {
            console.error("Error generating preview balls:", error);
        }
    }

    placePreviewBalls() {
        // Check if the game is over
        if (this.isGameOver()) {
            this.endGame();
            return;
        }

        const emptyCells = this.getEmptyCells();

        // Place preview balls
        for (let i = 0; i < Math.min(3, emptyCells.length); i++) {
            const index = Math.floor(Math.random() * emptyCells.length);
            const { row, col } = emptyCells[index];
            const color = this.previewBalls[i];

            // Place the ball
            this.createBall(row, col, color);

            // Remove the cell from the list
            emptyCells.splice(index, 1);

            // Check if this placement created a line
            const lines = this.checkLines(row, col);
            if (lines.length > 0) {
                this.removeLines(lines);
            }
        }

        // Generate new preview balls for next turn
        this.generatePreviewBalls();

        // Check if the game is over after placement
        if (this.isGameOver()) {
            this.endGame();
        }
    }

    isGameOver() {
        // Game is over when there are no empty cells
        return this.getEmptyCells().length === 0;
    }

    endGame() {
        // Show game over text
        this.gameOverText.setVisible(true);

        // Add a flash animation to the game over text
        this.tweens.add({
            targets: this.gameOverText,
            alpha: 0.5,
            yoyo: true,
            repeat: -1,
            duration: 1000
        });
    }

    update() {
        // Game update logic, if needed
    }

    // Update the score display
    updateScore() {
        try {
            const scoreElement = document.getElementById('score');
            if (scoreElement) {
                scoreElement.textContent = this.score;
            }
        } catch (error) {
            console.error("Error updating score:", error);
        }
    }

    updatePreviewBalls(previewColors) {
        const nextBallsContainer = document.getElementById('next-balls');
        if (!nextBallsContainer) return;
        nextBallsContainer.innerHTML = '';
        previewColors.forEach(color => {
            const ball = document.createElement('div');
            ball.classList.add('ball-preview', color);
            nextBallsContainer.appendChild(ball);
        });
    }
}
